(() => {
  if (window.rvProductDetailsLoaded) return;
  window.rvProductDetailsLoaded = true;
  const instances = new WeakMap();

  class ProductDetails {
    constructor(root) {
      this.root = root;
      this.controller = new AbortController();
      this.signal = this.controller.signal;
      this.motion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.slides = Array.from(root.querySelectorAll('[data-rv-pd-slide]'));
      this.thumbs = Array.from(root.querySelectorAll('[data-rv-pd-thumb]'));
      this.dots = Array.from(root.querySelectorAll('[data-rv-pd-dot]'));
      this.buy = root.querySelector('[data-rv-pd-buy]');
      this.form = root.querySelector('form[data-type="add-to-cart-form"]');
      this.button = root.querySelector('[data-rv-pd-atc]');
      this.idInput = root.querySelector('[data-rv-pd-variant-id]');
      this.quantityInput = this.form?.querySelector('[data-rv-pd-quantity]');
      this.bundle = root.querySelector('[data-rv-pd-bundle]');
      this.picker = root.querySelector('[data-rv-pd-picker]');
      this.variants = [];
      try { this.variants = JSON.parse(root.querySelector('[data-rv-pd-variants]')?.textContent || '[]'); } catch {}
      this.current = this.variants.find(v => String(v.id) === this.idInput?.value) || null;
      this.syncBundle(true);
      this.on(root, 'click', event => this.click(event));
      this.on(root, 'change', event => {
        if (event.target.matches('[data-rv-pd-option]')) this.selectOptions();
        if (event.target.matches('[data-rv-pd-bundle-option]')) this.syncBundle();
      });
      this.on(window, 'popstate', () => {
        if (!this.root.closest('quick-add-modal, [data-rv-quick-add]')) this.restoreUrlVariant();
      });
      if (this.form) this.on(this.form, 'submit', event => this.validateSelection(event), true);
      root.querySelectorAll('[data-rv-pd-faq-item]').forEach(item => this.setFaq(item, item.classList.contains('is-open')));
      this.resize = new ResizeObserver(() => {
        root.querySelectorAll('[data-rv-pd-faq-item].is-open').forEach(item => this.setFaq(item, true));
        root.querySelectorAll('[data-rv-pd-videos]').forEach(row => this.updateVideoArrows(row));
      });
      this.resize.observe(root);
      this.initVideos();
      this.initNotice();
      this.on(this.motion, 'change', () => {
        root.querySelectorAll('[data-rv-pd-video] video').forEach(video => {
          if (this.motion.matches) video.pause();
          else if (video.closest('[data-autoplay="true"]') && this.visibleVideos.has(video)) this.play(video);
        });
      });
    }

    on(target, name, handler, capture = false) {
      target.addEventListener(name, handler, { signal: this.signal, capture });
    }

    showMedia(index) {
      if (index < 0 || index >= this.slides.length) return;
      this.slides.forEach((slide, i) => {
        slide.classList.toggle('is-active', i === index);
        slide.setAttribute('aria-hidden', String(i !== index));
      });
      [...this.thumbs, ...this.dots].forEach(control => {
        const selected = Number(control.dataset.rvPdIndex) === index;
        control.classList.toggle('is-active', selected);
        if (selected) control.setAttribute('aria-current', 'true');
        else control.removeAttribute('aria-current');
      });
      const thumb = this.thumbs[index];
      const strip = this.root.querySelector('[data-rv-pd-thumbs]');
      if (thumb && strip) {
        const left = thumb.offsetLeft;
        if (left < strip.scrollLeft || left + thumb.offsetWidth > strip.scrollLeft + strip.clientWidth) {
          strip.scrollTo({ left: left - (strip.clientWidth - thumb.offsetWidth) / 2, behavior: this.motion.matches ? 'instant' : 'smooth' });
        }
      }
    }

    click(event) {
      const target = event.target.closest('button, [data-rv-pd-video-box]');
      if (!target || !this.root.contains(target)) return;
      if (target.matches('[data-rv-pd-thumb], [data-rv-pd-dot]')) this.showMedia(Number(target.dataset.rvPdIndex));
      if (target.matches('[data-rv-pd-prev], [data-rv-pd-next]')) {
        const index = this.slides.findIndex(slide => slide.classList.contains('is-active'));
        this.showMedia((index + (target.matches('[data-rv-pd-next]') ? 1 : -1) + this.slides.length) % this.slides.length);
      }
      if (target.matches('[data-rv-pd-faq-q]')) {
        const item = target.closest('[data-rv-pd-faq-item]');
        const open = !item.classList.contains('is-open');
        const faq = item.closest('[data-rv-pd-faq]');
        if (open && faq.dataset.accordion === 'true') faq.querySelectorAll('[data-rv-pd-faq-item]').forEach(other => this.setFaq(other, false));
        this.setFaq(item, open);
      }
      if (target.matches('[data-rv-pd-like]')) target.setAttribute('aria-pressed', String(target.getAttribute('aria-pressed') !== 'true'));
      if (target.matches('[data-rv-pd-video-box], [data-rv-pd-play]')) {
        const video = target.closest('[data-rv-pd-video]')?.querySelector('video');
        if (video) {
          video.dataset.rvUserPaused = String(!video.paused);
          if (video.paused) this.play(video);
          else video.pause();
        }
      }
      if (target.matches('[data-rv-pd-sound]')) {
        const video = target.closest('[data-rv-pd-video]')?.querySelector('video');
        if (video) {
          video.muted = !video.muted;
          target.setAttribute('aria-pressed', String(!video.muted));
          target.setAttribute('aria-label', video.muted ? 'Unmute video' : 'Mute video');
        }
      }
      if (target.matches('[data-rv-pd-videos-prev], [data-rv-pd-videos-next]')) {
        const track = target.closest('[data-rv-pd-videos]').querySelector('[data-rv-pd-track]');
        track.scrollBy({ left: track.clientWidth * (target.matches('[data-rv-pd-videos-next]') ? 1 : -1), behavior: this.motion.matches ? 'instant' : 'smooth' });
      }
    }

    selectOptions() {
      const groups = Array.from(this.root.querySelectorAll('[data-rv-pd-option-group]'));
      const values = groups.map(group => group.querySelector('input:checked')?.value);
      groups.forEach((group, index) => {
        const label = group.querySelector('[data-rv-pd-selected-label]');
        if (label && values[index]) label.textContent = values[index];
      });
      const variant = this.variants.find(candidate => candidate.options.every((value, i) => value === values[i]));
      this.applyVariant(variant || null, true);
    }

    applyVariant(variant, updateUrl = false) {
      const variantChanged = String(this.current?.id) !== String(variant?.id);
      this.current = variant;
      if (this.idInput) this.idInput.value = variant ? String(variant.id) : '';
      const error = this.root.querySelector('[data-rv-pd-error]');
      if (error) error.hidden = true;
      if (variantChanged) {
        // A rejected add belongs to the previous style, not the newly selected one.
        const productForm = this.form?.closest('product-form');
        if (typeof productForm?.handleErrorMessage === 'function') productForm.handleErrorMessage();
        else {
          const serverError = this.form?.querySelector('.product-form__error-message-wrapper');
          if (serverError) serverError.hidden = true;
        }
      }
      if (this.button && this.buy) {
        this.button.disabled = !variant?.available;
        const text = this.button.querySelector('[data-rv-pd-atc-text]');
        if (text) text.textContent = !variant ? this.buy.dataset.unavailableText : variant.available ? this.buy.dataset.addText : this.buy.dataset.soldOutText;
      }
      this.syncBundle(variantChanged);
      if (!variant) return;
      const price = this.root.querySelector('[data-rv-pd-price]');
      if (price) {
        const now = price.querySelector('[data-rv-pd-price-now]');
        const was = price.querySelector('[data-rv-pd-price-was]');
        const save = price.querySelector('[data-rv-pd-save]');
        const saveText = price.querySelector('[data-rv-pd-save-text]');
        if (now) now.textContent = variant.priceText;
        if (was) { was.textContent = variant.compareText; was.hidden = !(variant.compare > variant.price); }
        if (save) save.hidden = !(variant.compare > variant.price);
        if (saveText) saveText.textContent = `${price.dataset.saveLabel} ${variant.save}%`;
      }
      const mediaIndex = this.slides.findIndex(slide => String(slide.dataset.rvPdMedia) === String(variant.media));
      if (mediaIndex >= 0) this.showMedia(mediaIndex);
      if (updateUrl && !this.root.closest('quick-add-modal, [data-rv-quick-add]')) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState(window.history.state, '', url);
      }
      this.root.dispatchEvent(new CustomEvent('rv:variant-change', { bubbles: true, detail: { variant, sectionId: this.root.dataset.sectionId } }));
    }

    restoreUrlVariant() {
      const id = new URL(window.location.href).searchParams.get('variant');
      const variant = this.variants.find(v => String(v.id) === id) || this.variants.find(v => v.available) || this.variants[0];
      if (!variant) return;
      this.root.querySelectorAll('[data-rv-pd-option]').forEach(input => {
        input.checked = input.value === variant.options[Number(input.dataset.rvPdOption)];
      });
      this.selectOptions();
    }

    validateSelection(event) {
      // Reconcile the actual submitted quantity even after a browser restores form state.
      this.syncBundle();
      if (this.current?.available && this.idInput?.value) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      const error = this.root.querySelector('[data-rv-pd-error]');
      if (error) error.hidden = false;
      this.picker?.querySelector('input')?.focus();
    }

    syncBundle(reset = false) {
      const eligible = Boolean(this.bundle && this.quantityInput && this.current &&
        String(this.current.id) === this.bundle.dataset.eligibleVariant && this.current.bundlePriceText);
      if (this.bundle) {
        this.bundle.hidden = !eligible;
        this.bundle.querySelectorAll('[data-rv-pd-bundle-option]').forEach(input => {
          input.disabled = !eligible;
          if (reset || !eligible) input.checked = input.value === '1';
        });
        if (eligible) {
          const outputs = {
            '[data-rv-pd-bundle-single]': this.current.priceText,
            '[data-rv-pd-bundle-total]': this.current.bundlePriceText,
            '[data-rv-pd-bundle-was]': this.current.pairPriceText,
          };
          Object.entries(outputs).forEach(([selector, value]) => {
            const output = this.bundle.querySelector(selector);
            if (output) output.textContent = value;
          });
        }
      }
      const quantity = eligible && this.bundle.querySelector('[data-rv-pd-bundle-option]:checked')?.value === '2' ? '2' : '1';
      if (this.quantityInput) this.quantityInput.value = quantity;
      const buttonPrice = this.root.querySelector('[data-rv-pd-atc-price]');
      if (buttonPrice && this.current) buttonPrice.textContent = quantity === '2' ? this.current.bundlePriceText : this.current.priceText;
    }

    setFaq(item, open) {
      item.classList.toggle('is-open', open);
      const answer = item.querySelector('[data-rv-pd-faq-a]');
      item.querySelector('[data-rv-pd-faq-q]').setAttribute('aria-expanded', String(open));
      answer.setAttribute('aria-hidden', String(!open));
      answer.inert = !open;
      answer.style.maxHeight = open ? `${answer.firstElementChild.scrollHeight + 16}px` : '0px';
    }

    play(video) {
      const promise = video.play();
      if (promise?.catch) promise.catch(() => video.closest('[data-rv-pd-video]').classList.remove('is-playing'));
    }

    initVideos() {
      this.visibleVideos = new Set();
      this.videoObserver = new IntersectionObserver(entries => {
        entries.forEach(entry => {
          const video = entry.target;
          if (entry.isIntersecting) {
            this.visibleVideos.add(video);
            if (!this.motion.matches && video.dataset.rvUserPaused !== 'true' && video.closest('[data-autoplay="true"]')) this.play(video);
          } else {
            this.visibleVideos.delete(video);
            video.pause();
          }
        });
      }, { threshold: 0.2 });
      this.root.querySelectorAll('[data-rv-pd-video] video').forEach(video => {
        video.muted = true;
        if (this.motion.matches) { video.autoplay = false; video.pause(); }
        const item = video.closest('[data-rv-pd-video]');
        this.on(video, 'play', () => item.classList.add('is-playing'));
        this.on(video, 'pause', () => item.classList.remove('is-playing'));
        this.on(video, 'ended', () => item.classList.remove('is-playing'));
        this.videoObserver.observe(video);
      });
      this.on(document, 'visibilitychange', () => {
        this.visibleVideos.forEach(video => {
          if (document.hidden) video.pause();
          else if (!this.motion.matches && video.dataset.rvUserPaused !== 'true' && video.closest('[data-autoplay="true"]')) this.play(video);
        });
      });
      this.root.querySelectorAll('[data-rv-pd-videos]').forEach(row => this.updateVideoArrows(row));
    }

    updateVideoArrows(row) {
      const track = row.querySelector('[data-rv-pd-track]');
      row.querySelector('.rv-pd__videos-scroller')?.classList.toggle('has-overflow', track.scrollWidth > track.clientWidth + 1);
    }

    initNotice() {
      this.notices = Array.from(this.root.querySelectorAll('[data-rv-pd-notice]')).map(notice => {
        const min = Number(notice.dataset.min) || 25;
        const max = Math.max(min, Number(notice.dataset.max) || 60);
        return { output: notice.querySelector('[data-rv-pd-count]'), min, max, value: Math.floor((min + max) / 2) };
      });
      if (!this.notices.length) return;
      this.noticeTimer = window.setInterval(() => {
        if (document.hidden) return;
        this.notices.forEach(notice => {
          const direction = Math.random() < 0.5 ? -1 : 1;
          notice.value = Math.max(notice.min, Math.min(notice.max, notice.value + direction * (1 + Math.floor(Math.random() * 3))));
          if (notice.output) notice.output.textContent = notice.value;
        });
      }, 6000);
    }

    destroy() {
      this.controller.abort();
      this.resize.disconnect();
      this.videoObserver.disconnect();
      window.clearInterval(this.noticeTimer);
      this.root.querySelectorAll('[data-rv-pd-video] video').forEach(video => video.pause());
      instances.delete(this.root);
    }
  }

  function initialize(scope) {
    const roots = scope.matches?.('[data-rv-pd]') ? [scope] : scope.querySelectorAll('[data-rv-pd]');
    roots.forEach(root => { if (!instances.has(root)) instances.set(root, new ProductDetails(root)); });
  }
  function destroy(scope) {
    const roots = scope.matches?.('[data-rv-pd]') ? [scope] : scope.querySelectorAll('[data-rv-pd]');
    roots.forEach(root => instances.get(root)?.destroy());
  }
  window.rvProductDetails = { initialize, destroy };
  document.addEventListener('rv:product-details:init', event => initialize(event.target));
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initialize(document), { once: true });
  else initialize(document);
  document.addEventListener('shopify:section:load', event => initialize(event.target));
  document.addEventListener('shopify:section:unload', event => destroy(event.target));
})();
